// store/actions/user.js

import { userActionTypes } from './actionTypes';
import { queryClient, PERSIST_CACHE_KEY } from '../../config/queryClient';

const getApiBaseUrl = () => {
  console.log('ENV Check:', {
    REACT_APP_API_URL: process.env.REACT_APP_API_URL,
    NODE_ENV: process.env.NODE_ENV
  });

  // If explicitly set, use that
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Otherwise, use environment-based defaults
  if (process.env.NODE_ENV === 'production') {
    return 'https://pulse-demo-api-342707009361.us-central1.run.app/';
  } else {
    return 'http://localhost:8000';
  }
};

const API_BASE_URL = getApiBaseUrl();
// const API_BASE_URL = 'http://localhost:8000';

const loginStart = () => {
  return {
    type: userActionTypes.LOGIN_START,
  };
};

const loginSuccess = (data) => {

  // CRITICAL FIX: Store token in localStorage for apiService
  if (data?.user?.token) {
    localStorage.setItem('auth_token', data.user.token);
    // console.log('JWT token stored in localStorage for apiService');
  }

  // Real (non-guest) login — make sure a stale is_guest flag from a prior
  // guest session doesn't keep the user in guest mode.
  localStorage.removeItem('is_guest');

  // Internal-domain rule: anyone on @scaletime.co is treated as admin,
  // regardless of what the backend returns. Guest flow has its own path.
  const email = data['user']['email'] || '';
  const isInternal = email.toLowerCase().endsWith('@scaletime.co');
  const role = isInternal ? 'admin' : data['user']['role'];

  return {
    type: userActionTypes.LOGIN_SUCCESS,
    name: data['user']['name'],
    email: data['user']['email'],
    role,
    picture: data['user']['profile_picture'],
    qb_company_id: data['user']['qb_company_id'],
    qb_access_token: data['user']['qb_access_token'],
    token: data['user']['token'],
  };
};

const loginError = (errorMessage) => {
  return {
    type: userActionTypes.LOGIN_ERROR,
    errorMessage: errorMessage,
  };
};

const doLogout = () => {
  // console.log('in doLogout');
  return {
    type: userActionTypes.LOGOUT,
  };
};


export const handleGoogleLogin = (token) => {
  return async (dispatch) => {
    dispatch(loginStart());
    try {
      // console.log('Authenticating with Google token');
      
      const response = await fetch(`${API_BASE_URL}/user_auth/google-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: token
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Authentication failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      // console.log("Backend response:", data);
      
      if (data.access_token && data.user) {
        const loginData = {
          user: {
            name: data.user.name,
            email: data.user.email,
            role: data.user.role,
            profile_picture: data.user.profile_picture,
            qb_company_id: data.user.qb_company_id,
            qb_access_token: data.user.qb_access_token,
            token: data.access_token // JWT token from backend
          }
        };
        
        // console.log('JWT token received:', data.access_token.substring(0, 30) + '...');
        dispatch(loginSuccess(loginData));
        
      } else {
        console.error('Invalid response structure:', data);
        throw new Error('Invalid response structure from backend');
      }
      
    } catch (error) {
      console.error('Authentication error:', error);
      dispatch(loginError(error.message || 'Authentication failed'));
    }
  };
};


// UPDATED: Handle credential login with JWT
export const handleCredentialLogin = (credentials) => {
  return async (dispatch) => {
    dispatch(loginStart());
    try {
      // console.log('Authenticating with email/password');
      
      const response = await fetch(`${API_BASE_URL}/user_auth/credential-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Authentication failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log("Credential auth response:", data);
      
      if (data.access_token && data.user) {
        const loginData = {
          user: {
            name: data.user.name,
            email: data.user.email,
            role: data.user.role,
            profile_picture: data.user.profile_picture,
            qb_company_id: data.user.qb_company_id,
            qb_access_token: data.user.qb_access_token,
            token: data.access_token
          }
        };
        
        // console.log('JWT token received from credential login');
        
        dispatch(loginSuccess(loginData));
        
      } else {
        throw new Error('Invalid response structure from backend');
      }
      
    } catch (error) {
      console.error('Credential authentication error:', error);
      dispatch(loginError(error.message || 'Authentication failed'));
      throw new Error(error.message);
    }
  };
};

export const logout = () => {
  return async (dispatch) => {
    try {
      // Clear token and guest flag from localStorage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('is_guest');
      sessionStorage.removeItem('auth_token');

      // Clear react-query cache so the next user doesn't see prior user's data
      queryClient.clear();
      localStorage.removeItem(PERSIST_CACHE_KEY);

      dispatch(doLogout());
    } catch (e) {
      console.error('Logout error:', e);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('is_guest');
      sessionStorage.removeItem('auth_token');
      queryClient.clear();
      localStorage.removeItem(PERSIST_CACHE_KEY);
      dispatch(doLogout());
    }
  };
};

// Add this with your other action creators
const loginGuestStart = () => {
  return {
    type: userActionTypes.LOGIN_GUEST,
  };
};

const loginGuestSuccess = (data) => {
  // console.log("✅ Guest login success data:", data);
  
  // Store guest token in localStorage
  if (data?.token) {
    localStorage.setItem('auth_token', data.token);
    localStorage.setItem('is_guest', 'true');
    // console.log('✅ Guest session token stored');
  }
  
  return {
    type: userActionTypes.LOGIN_GUEST_SUCCESS,
    name: data.name,
    email: data.email,
    role: data.role,
    picture: data.profile_picture || "",
    token: data.token,
    isGuest: true,
    // ADD THESE MISSING FIELDS to match loginSuccess structure:
    qb_company_id: null,
    qb_access_token: null,
  };
};

const loginGuestError = (errorMessage) => {
  return {
    type: userActionTypes.LOGIN_GUEST_ERROR,
    errorMessage: errorMessage,
  };
};

// Guest login handler - FIXED VERSION
export const handleGuestLogin = () => {
  return async (dispatch) => {
    dispatch(loginGuestStart());
    try { 
      const response = await fetch(`${API_BASE_URL}/user_auth/guest-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: 'guest@scaletime.co'
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Guest auth failed:', errorText);
        throw new Error(`Guest authentication failed: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      
      // THE KEY FIX: Backend returns { user, access_token, ... }
      // We need to map it to what Redux expects
      if (data.access_token && data.user) {
        const guestData = {
          name: data.user.name,
          email: data.user.email,
          role: data.user.role || 'admin',  // Use role from backend
          profile_picture: data.user.profile_picture || "",
          token: data.access_token,  // ← Backend calls it 'access_token'
          isGuest: true
        };
        
        dispatch(loginGuestSuccess(guestData));
        
      } else {
        console.error('❌ Invalid response structure:', data);
        throw new Error('Invalid response structure from backend');
      }
      
    } catch (error) {
      console.error('❌ Guest authentication error:', error);
      dispatch(loginGuestError(error.message || 'Guest login failed'));
      throw error;
    }
  };
};


export const setQbConnected = (isConnected) => {
  return {
    type: userActionTypes.SET_QB_CONNECTED,
    payload: isConnected,
  };
};